import { zodResolver } from '@hookform/resolvers/zod';
import {
    Accordion, AccordionDetails, AccordionSummary, Box, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, Grid, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useEffect, useMemo, useState } from 'react';
import { Form } from '@components/Form';
import MDButton from '@components/MDButton';
import { smallDialog } from 'assets/theme/dialogSize';
import { setLoading, useMaterialUIController } from 'context';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@functions/api-error-message';
import { Combo } from 'types/combo';
import { CrmPhoneField } from '../../kanban-board/components/shared/crm-phone-field';
import { onlyDigits } from '../../kanban-board/formatters/crm-phone-mask';
import { CompanyService } from '../../companies/services/company.service';
import { CompanyOptionResponse } from '../../companies/services/responses/company.response';
import { companyOptionLabel } from '../../companies/company-form';
import { formatCpf, isValidCpf } from '@functions/brazil-documents';
import { MaskedField } from '@components/MaskedField';
import { PersonService } from '../services/person.service';
import { EMaritalStatus, EPersonSex, PersonResponse } from '../services/responses/person.response';
import {
    ADDRESS_FIELDS, addressFromForm, addressSchema, addressToForm, EMPTY_ADDRESS, refineAddress,
} from '../address-form';
import { AddressFields } from './address-fields';
import { firstErrorMessage } from '@functions/first-error-message';

const MIN_PHONE_DIGITS = 10;

type Section = 'dados' | 'endereco';

const SEX_OPTIONS = [
    { value: EPersonSex.Male, label: 'people.sexoMasculino' },
    { value: EPersonSex.Female, label: 'people.sexoFeminino' },
];

const MARITAL_STATUS_OPTIONS = [
    { value: EMaritalStatus.Single, label: 'people.estadoCivilSolteiro' },
    { value: EMaritalStatus.Married, label: 'people.estadoCivilCasado' },
    { value: EMaritalStatus.StableUnion, label: 'people.estadoCivilUniaoEstavel' },
    { value: EMaritalStatus.Divorced, label: 'people.estadoCivilDivorciado' },
    { value: EMaritalStatus.Widowed, label: 'people.estadoCivilViuvo' },
];

/// Telefone de recado é opcional, mas se vier precisa de DDD e número.
const optionalPhone = (value?: string) => !value || onlyDigits(value).length >= MIN_PHONE_DIGITS;

const today = () => new Date().toISOString().slice(0, 10);

interface Props {
    open: boolean;
    edit: PersonResponse | null;
    handleModal: () => void;
    getAll: () => void;
    /// Quem abriu o modal para cadastrar e já usar a pessoa (o modal de lead).
    onSaved?: (person: PersonResponse) => void;
}

export const PeopleModal = ({ open, edit, handleModal, getAll, onSaved }: Props) => {
    const { t } = useTranslation();
    const [, dispatch] = useMaterialUIController();
    const { showAlert } = useAlert();

    const [opened, setOpened] = useState<Section[]>(['dados']);

    const [companies, setCompanies] = useState<CompanyOptionResponse[]>([]);

    useEffect(() => {
        if (!open) return;

        new CompanyService()
            .options()
            .then((res) => setCompanies(res.data.data ?? []))
            // Acessório: falhar aqui não pode impedir o cadastro da pessoa.
            .catch(() => setCompanies([]));
    }, [open]);

    /// A primeira opção desliga a pessoa da empresa: o seletor não tem botão de limpar.
    const companyOptions: Combo[] = useMemo(
        () => [
            { value: '', label: t('people.semEmpresa') },
            ...companies.map((company) => ({ value: company.id, label: companyOptionLabel(company) })),
        ],
        [companies, t]);

    /// A primeira opção limpa o campo, como "Sem empresa": o seletor não tem botão de limpar.
    const withNotInformed = (options: { value: number; label: string }[]): Combo[] => [
        { value: '', label: t('people.naoInformado') },
        ...options.map((option) => ({ value: String(option.value), label: t(option.label) })),
    ];

    const toggle = (section: Section) => setOpened((current) => (
        current.includes(section) ? current.filter((item) => item !== section) : [...current, section]));

    /// Contato do Instagram ou da importação pode não ter número: na edição dele o telefone é opcional.
    const phoneRequired = !edit || onlyDigits(edit.phone ?? '').length > 0;

    const schema = z
        .object({
            name: z.string().min(1, t('validacao.obrigatorio')).max(150),
            phone: z
                .string()
                .refine((value) => !phoneRequired || onlyDigits(value).length > 0, t('validacao.obrigatorio'))
                .refine(
                    (value) => !onlyDigits(value) || onlyDigits(value).length >= MIN_PHONE_DIGITS,
                    t('people.telefoneIncompleto')
                ),
            email: z.string().email().max(150).optional().or(z.literal('')),
            notes: z.string().max(1000).optional(),
            companyId: z.string().nullable().optional(),
            birthDate: z
                .string()
                .optional()
                .refine((value) => !value || value <= today(), t('people.nascimentoFuturo')),
            sex: z.string().nullable().optional(),
            cpf: z
                .string()
                .optional()
                .refine((value) => !value || isValidCpf(value), t('people.cpfInvalido')),
            maritalStatus: z.string().nullable().optional(),
            secondContactName: z.string().max(150).optional(),
            secondContactPhone: z.string().optional().refine(optionalPhone, t('people.telefoneIncompleto')),
            ...addressSchema,
        })
        .superRefine(refineAddress(t));

    type PersonForm = z.infer<typeof schema>;

    const empty: PersonForm = {
        name: '', phone: '', email: '', notes: '', companyId: '',
        birthDate: '', sex: '', cpf: '', maritalStatus: '', secondContactName: '', secondContactPhone: '',
        ...EMPTY_ADDRESS,
    };

    const form = useForm<PersonForm>({
        resolver: zodResolver(schema),
        defaultValues: empty,
    });

    const { handleSubmit, reset, formState: { errors } } = form;

    useEffect(() => {
        if (!open) return;

        reset({
            ...empty,
            name: edit?.name ?? '',
            phone: edit ? onlyDigits(edit.phone) : '',
            email: edit?.email ?? '',
            notes: edit?.notes ?? '',
            companyId: edit?.companyId ?? '',
            birthDate: edit?.profile?.birthDate ?? '',
            sex: edit?.profile?.sex ? String(edit.profile.sex) : '',
            cpf: edit?.profile?.cpf ?? '',
            maritalStatus: edit?.profile?.maritalStatus ? String(edit.profile.maritalStatus) : '',
            secondContactName: edit?.profile?.secondContactName ?? '',
            secondContactPhone: onlyDigits(edit?.profile?.secondContactPhone ?? ''),
            ...addressToForm(edit?.address),
        });

        // Na edição, o que já tem dado aparece aberto; o resto fica fechado.
        setOpened(['dados', ...(edit?.address ? ['endereco' as const] : [])]);
    }, [open, edit]);

    useEffect(() => {
        const fields = Object.keys(errors);
        if (fields.length === 0) return;

        showAlert(firstErrorMessage(errors) ?? t('general.camposObrigatorios'), 'error', 2500);

        // Campo inválido numa seção fechada faria o salvar parecer que não funciona.
        const withError: Section[] = fields.some((field) => (ADDRESS_FIELDS as readonly string[]).includes(field))
            ? ['endereco']
            : ['dados'];
        setOpened((current) => [...new Set([...current, ...withError])]);
    }, [errors]);

    const save = async (data: PersonForm) => {
        setLoading(dispatch, true);
        try {
            const body = {
                name: data.name.trim(),
                phone: onlyDigits(data.phone),
                email: data.email || undefined,
                notes: data.notes || undefined,
                companyId: data.companyId || null,
                address: addressFromForm(data),
                profile: {
                    birthDate: data.birthDate || null,
                    sex: data.sex ? Number(data.sex) : null,
                    cpf: onlyDigits(data.cpf) || null,
                    maritalStatus: data.maritalStatus ? Number(data.maritalStatus) : null,
                    secondContactName: data.secondContactName?.trim() || null,
                    secondContactPhone: onlyDigits(data.secondContactPhone) || null,
                },
            };

            const service = new PersonService();
            const res = edit ? await service.update(edit.id, body) : await service.create(body);

            showAlert(edit ? t('general.atualizacao') : t('general.cadastro'), 'success', 2000);
            getAll();
            onSaved?.(res.data.data);
            handleModal();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 3000);
        } finally {
            setLoading(dispatch, false);
        }
    };

    const section = (key: Section, title: string, content: React.ReactNode) => (
        <Accordion expanded={opened.includes(key)} onChange={() => toggle(key)} disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">{title}</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container spacing={2}>{content}</Grid>
            </AccordionDetails>
        </Accordion>
    );

    return (
        <Dialog open={open} onClose={handleModal} fullWidth maxWidth="xl" scroll="paper">
            <DialogTitle>{edit ? t('people.editarPessoa') : t('people.novaPessoa')}</DialogTitle>
            <FormProvider {...form}>
                <form onSubmit={handleSubmit(save)}>
                    <DialogContent style={smallDialog} sx={{ display: 'grid', gap: 1.5 }}>
                        {section('dados', t('people.dadosEContato'), (
                            <>
                                <Form.Field size={6}>
                                    <FormControl fullWidth>
                                        <Form.Label>
                                            {t('general.nome')}
                                            <Form.MandatoryIcon />
                                        </Form.Label>
                                        <Form.TextFields name="name" size="small" inputProps={{ maxLength: 150 }} placeholder={t('people.phNome')} />
                                        <Form.ErrorMessage field="name" />
                                    </FormControl>
                                </Form.Field>
                                <Form.Field size={3}>
                                    <FormControl fullWidth>
                                        <Form.Label>
                                            {t('people.telefone')}
                                            {phoneRequired && <Form.MandatoryIcon />}
                                        </Form.Label>
                                        <CrmPhoneField name="phone" size="small" fullWidth placeholder={t('people.phTelefone')} />
                                        <Form.ErrorMessage field="phone" />
                                    </FormControl>
                                </Form.Field>
                                <Form.Field size={3}>
                                    <FormControl fullWidth>
                                        <Form.Label>{t('people.email')}</Form.Label>
                                        <Form.TextFields name="email" size="small" inputProps={{ maxLength: 150 }} placeholder={t('people.phEmail')} />
                                        <Form.ErrorMessage field="email" />
                                    </FormControl>
                                </Form.Field>
                                <Form.Field size={3}>
                                    <FormControl fullWidth>
                                        <Form.Label>{t('people.dataNascimento')}</Form.Label>
                                        <Form.TextFields
                                            name="birthDate"
                                            type="date"
                                            size="small"
                                            inputProps={{ max: today() }}
                                        />
                                        <Form.ErrorMessage field="birthDate" />
                                    </FormControl>
                                </Form.Field>
                                <Form.Field size={3}>
                                    <FormControl fullWidth>
                                        <Form.Label>{t('people.sexo')}</Form.Label>
                                        <Form.AutoCompleteSelect
                                            name="sex" size="small"
                                            options={withNotInformed(SEX_OPTIONS)}
                                        />
                                    </FormControl>
                                </Form.Field>
                                <Form.Field size={3}>
                                    <FormControl fullWidth>
                                        <Form.Label>{t('people.cpf')}</Form.Label>
                                        <MaskedField
                                            name="cpf"
                                            size="small"
                                            fullWidth
                                            mask={formatCpf}
                                            maxDigits={11}
                                            placeholder="000.000.000-00"
                                        />
                                        <Form.ErrorMessage field="cpf" />
                                    </FormControl>
                                </Form.Field>
                                <Form.Field size={3}>
                                    <FormControl fullWidth>
                                        <Form.Label>{t('people.estadoCivil')}</Form.Label>
                                        <Form.AutoCompleteSelect
                                            name="maritalStatus" size="small"
                                            options={withNotInformed(MARITAL_STATUS_OPTIONS)}
                                        />
                                    </FormControl>
                                </Form.Field>
                                <Form.Field size={3}>
                                    <FormControl fullWidth>
                                        <Form.Label>{t('people.nomeContato2')}</Form.Label>
                                        <Form.TextFields name="secondContactName" size="small" inputProps={{ maxLength: 150 }} />
                                        <Form.ErrorMessage field="secondContactName" />
                                    </FormControl>
                                </Form.Field>
                                <Form.Field size={3}>
                                    <FormControl fullWidth>
                                        <Form.Label>{t('people.telefoneContato2')}</Form.Label>
                                        <CrmPhoneField name="secondContactPhone" size="small" fullWidth />
                                        <Form.ErrorMessage field="secondContactPhone" />
                                    </FormControl>
                                </Form.Field>
                                <Form.Field size={6}>
                                    <FormControl fullWidth>
                                        <Form.Label>{t('people.empresa')}</Form.Label>
                                        <Form.AutoCompleteSelect name="companyId" options={companyOptions} size="small" />
                                    </FormControl>
                                </Form.Field>
                                <Form.Field size={12}>
                                    <FormControl fullWidth>
                                        <Form.Label>{t('people.observacoes')}</Form.Label>
                                        <Form.TextFields
                                            name="notes"
                                            placeholder={t('people.phObservacoes')}
                                            multiline
                                            minRows={3}
                                            size="small"
                                            inputProps={{ maxLength: 1000 }}
                                        />
                                    </FormControl>
                                </Form.Field>
                            </>
                        ))}

                        {section('endereco', t('people.endereco'), <AddressFields />)}
                    </DialogContent>
                    <DialogActions sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Box>
                            <MDButton sx={{ marginRight: '5px' }} variant="gradient" color="dark" onClick={handleModal}>
                                {t('general.cancelar')}
                            </MDButton>
                            <MDButton variant="gradient" color="success" type="submit">
                                {t('general.salvar')}
                            </MDButton>
                        </Box>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};
